import CamelCaseTransform from 'cio/transforms/camel-case';
import {
  BaseMessagingChannel,
  MessagingChannelType,
  MessagingChannel,
  MessagingChannelPushCompletionReason,
} from 'cio/utils/setup-guide/messaging-channels';

interface BaseSerializedMessagingChannel {
  type: MessagingChannelType;
  selected_at: number;
  completed_at: null | number;
}
interface SerializedMessagingChannelSlack extends BaseSerializedMessagingChannel {
  type: MessagingChannelType.Slack;
}
interface SerializedMessagingChannelTwilio extends BaseSerializedMessagingChannel {
  type: MessagingChannelType.Twilio;
}
interface SerializedMessagingChannelWebhook extends BaseSerializedMessagingChannel {
  type: MessagingChannelType.Webhook;
}
interface SerializedMessagingChannelInApp extends BaseSerializedMessagingChannel {
  type: MessagingChannelType.InApp;
}
interface SerializedMessagingChannelEmail extends BaseSerializedMessagingChannel {
  type: MessagingChannelType.Email;
  skipped_link_tracking_for_domains?: string[];
  completed_universal_link_tracking_at: number;
  skipped_universal_link_tracking_at: number;
}

interface SerializedMessagingChannelPushPlatform {
  skipped_at: number;
  completed_at: number;
  completion_reason: MessagingChannelPushCompletionReason;
}

interface SerializedMessagingChannelPush extends BaseSerializedMessagingChannel {
  type: MessagingChannelType.Push;
  ios: SerializedMessagingChannelPushPlatform;
  android: SerializedMessagingChannelPushPlatform;
}

type SerializedMessagingChannel =
  | SerializedMessagingChannelSlack
  | SerializedMessagingChannelTwilio
  | SerializedMessagingChannelWebhook
  | SerializedMessagingChannelEmail
  | SerializedMessagingChannelPush
  | SerializedMessagingChannelInApp;

// Takes the naive camelcased version of the data and applies default values, if
// necessary
function normalizeCamelCasedChannel(deserialized: MessagingChannel): MessagingChannel {
  let base: BaseMessagingChannel = {
    type: deserialized.type,
    selectedAt: deserialized.selectedAt,
    completedAt: deserialized.completedAt,
  };

  switch (deserialized.type) {
    case MessagingChannelType.Email:
      return {
        ...base,
        type: MessagingChannelType.Email,
        skippedLinkTrackingForDomains: deserialized.skippedLinkTrackingForDomains ?? [],
        completedUniversalLinkTrackingAt: deserialized.completedUniversalLinkTrackingAt ?? undefined,
        skippedUniversalLinkTrackingAt: deserialized.skippedUniversalLinkTrackingAt ?? undefined,
      };
    case MessagingChannelType.Push:
      return {
        ...base,
        type: MessagingChannelType.Push,
        ios: deserialized.ios,
        android: deserialized.android,
      };
    default:
      return base as MessagingChannel;
  }
}

export default class SetupGuideMessagingChannelsTransform extends CamelCaseTransform {
  deserialize(serialized: SerializedMessagingChannel[] | null, options: any) {
    let deserialized = super.deserialize(serialized, { ...options, recursive: true }) as MessagingChannel[] | null;

    if (deserialized == null) {
      return [];
    }

    return deserialized.map((channel) => normalizeCamelCasedChannel(channel));
  }

  serialize(deserialized: MessagingChannel[] | null, options: any) {
    let serialized = super.serialize(deserialized, { ...options, recursive: true });

    if (serialized == null) {
      return [];
    }

    return serialized;
  }
}

declare module 'ember-data/types/registries/transform' {
  export default interface TransformRegistry {
    'setup-guide/messaging-channels': MessagingChannel[] | null;
  }
}
